'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { OZ } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import Loadable from 'UIComponents/decorators/Loadable';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import H5 from 'UIComponents/elements/headings/H5';
import UIButton from 'UIComponents/button/UIButton';
import UICopyButton from 'UIComponents/button/UICopyButton';
import UISelectableButton from 'UIComponents/button/UISelectableButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIllustration from 'UIComponents/image/UIIllustration';
import UILogo from 'UIComponents/image/UILogo';
import UITag from 'UIComponents/tag/UITag';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIList from 'UIComponents/list/UIList';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UISection from 'UIComponents/section/UISection';
import * as ScreenSizes from '../../constants/layout/ScreenSizes';
import MediaQuery from '../layout/MediaQuery';
import validSupportedLanguages from '../../constants/groupTags/SupportedLanguages';
import SupportInfo from '../../records/SupportInfo';
import BigText from '../elements/BigText';
import ThemeProviderProfile from './ThemeProviderProfile';
import ThemeProviderDetailsResponse from '../../records/ThemeProviderDetailsResponse';
const SUPPORT_LINK_BUTTON_WIDTH = 184;
const SUPPORT_LINK_IMAGE_HEIGHT = 72;
const AsyncThemeSupportBusinessHours = Loadable({
  loader: () => import(
  /* webpackChunkName: 'theme-support-business-hours' */
  './ThemeSupportBusinessHours'),
  LoadingComponent: ({
    pastDelay
  }) => pastDelay ? /*#__PURE__*/_jsx(UILoadingSpinner, {
    size: "extra-small"
  }) : null,
  ErrorComponent: () => /*#__PURE__*/_jsx(AbstractTextElement, {
    use: "error",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystemMarketplaceCore.themes.details.support.businessHours.loadingError"
    })
  })
});

const SectionTitle = ({
  children
}) => /*#__PURE__*/_jsx(H5, {
  className: "m-bottom-3",
  children: children
});

const StyledSupportLinkButton = styled(props => {
  const {
    Button = UIButton,
    buttonProps = {}
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["Button", "buttonProps"]);

  return /*#__PURE__*/_jsx(Button, Object.assign({}, rest, {}, buttonProps));
}).withConfig({
  displayName: "ThemeSupport__StyledSupportLinkButton",
  componentId: "sc-1in8lds-0"
})(["display:flex;align-items:stretch;margin-left:12px;margin-top:12px;&&:hover{text-decoration:none;}"]);

const SupportLinkBox = (_ref) => {
  let {
    Button,
    buttonProps,
    children,
    href,
    illustrationName,
    textLabel
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["Button", "buttonProps", "children", "href", "illustrationName", "textLabel"]);

  return /*#__PURE__*/_jsx(StyledSupportLinkButton, Object.assign({}, rest, {
    Button: Button,
    buttonProps: buttonProps,
    iconProps: {
      className: 'hidden'
    },
    external: true,
    href: href,
    use: "unstyled",
    children: /*#__PURE__*/_jsx(UISelectableButton, {
      selected: false,
      size: "large",
      textLabel: textLabel,
      width: SUPPORT_LINK_BUTTON_WIDTH,
      children: children || /*#__PURE__*/_jsx(UIIllustration, {
        height: SUPPORT_LINK_IMAGE_HEIGHT,
        name: illustrationName
      })
    })
  }));
};

const defaultLanguageTagRenderer = langCode => /*#__PURE__*/_jsx(UITag, {
  children: /*#__PURE__*/_jsx(FormattedMessage, {
    message: `SharedI18nStrings.languageNames.${langCode}`
  })
}, `supported-language-${langCode}`);

const ThemeSupport = ({
  chatUrl,
  facebookUrl,
  getThemeEventSelectorId = () => undefined,
  languageTagRenderer = defaultLanguageTagRenderer,
  onTrackInteraction,
  phone,
  provider = {},
  responsive,
  support = {},
  supportEmail,
  supportLanguages,
  supportUrl
}) => {
  const trackInteraction = useCallback((...args) => {
    if (onTrackInteraction) {
      onTrackInteraction(...args);
    }
  }, [onTrackInteraction]);
  const {
    description,
    name
  } = provider;
  const [phoneNumber, phoneExtension] = phone ? phone.split('-') : [];
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(ThemeProviderProfile, {
        onTrackInteraction: trackInteraction,
        providerAvatarSize: 60,
        providerWebsiteAnalyticsId: getThemeEventSelectorId('SUPPORT-PROVIDER-WEBSITE'),
        provider: provider
      })
    }), /*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(BigText, {
        children: description
      })
    }), support && !!support.answers.size && /*#__PURE__*/_jsxs(UISection, {
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystemMarketplaceCore.themes.details.support.supportIncludes.title"
        })
      }), /*#__PURE__*/_jsx(UIList, {
        styled: true,
        children: support.answers.map((answerText, index) => /*#__PURE__*/_jsx(BigText, {
          children: answerText
        }, `support-answer-${index}`))
      })]
    }), support && !!support.responseTime && /*#__PURE__*/_jsxs(UISection, {
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystemMarketplaceCore.themes.details.support.responseTime.title"
        })
      }), /*#__PURE__*/_jsx(BigText, {
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "ecosystemMarketplaceCore.themes.details.support.responseTime.content",
          options: {
            providerName: name,
            responseTime: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `ecosystemMarketplaceCore.responseTime.${support.responseTime}`
            })
          }
        })
      })]
    }), support && !!support.hours.size && /*#__PURE__*/_jsxs(UISection, {
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystemMarketplaceCore.themes.details.support.businessHours.title"
        })
      }), /*#__PURE__*/_jsx(BigText, {
        children: /*#__PURE__*/_jsx(AsyncThemeSupportBusinessHours, {
          hours: support.hours.toArray(),
          timezone: support.timezone
        })
      })]
    }), /*#__PURE__*/_jsxs(UISection, {
      children: [/*#__PURE__*/_jsx(SectionTitle, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystemMarketplaceCore.themes.details.support.supportLanguages.title"
        })
      }), /*#__PURE__*/_jsx(UIList, {
        inline: true,
        children: supportLanguages.map(languageTagRenderer)
      })]
    }), /*#__PURE__*/_jsx(MediaQuery, {
      minScreenSize: responsive ? ScreenSizes.MD : ScreenSizes.XS,
      children: /*#__PURE__*/_jsxs(UISection, {
        children: [/*#__PURE__*/_jsx(SectionTitle, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ecosystemMarketplaceCore.themes.details.support.contact.title",
            options: {
              providerName: name
            }
          })
        }), /*#__PURE__*/_jsxs(UIFlex, {
          align: "stretch",
          wrap: "wrap",
          style: {
            marginLeft: -12,
            marginTop: -12
          },
          children: [supportUrl && /*#__PURE__*/_jsx(SupportLinkBox, {
            "data-hs-analytics-id": getThemeEventSelectorId('SUPPORT-SUPPORT-WEBSITE'),
            href: supportUrl,
            illustrationName: "add-on-website",
            onClick: () => trackInteraction('click support website link'),
            textLabel: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystemMarketplaceCore.themes.details.support.contact.supportUrl"
            })
          }), /*#__PURE__*/_jsx(SupportLinkBox, {
            "data-hs-analytics-id": getThemeEventSelectorId('SUPPORT-EMAIL'),
            Button: UICopyButton,
            buttonProps: {
              value: supportEmail
            },
            illustrationName: "successful-send",
            onClick: () => trackInteraction('click copy email address'),
            textLabel: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystemMarketplaceCore.themes.details.support.contact.supportEmail"
            })
          }), chatUrl && /*#__PURE__*/_jsx(SupportLinkBox, {
            "data-hs-analytics-id": getThemeEventSelectorId('SUPPORT-CHAT'),
            href: chatUrl,
            illustrationName: "messages",
            onClick: () => trackInteraction('click live chat link'),
            textLabel: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystemMarketplaceCore.themes.details.support.contact.chatUrl"
            })
          }), facebookUrl && /*#__PURE__*/_jsx(SupportLinkBox, {
            "data-hs-analytics-id": getThemeEventSelectorId('SUPPORT-FACEBOOK'),
            href: facebookUrl,
            onClick: () => trackInteraction('click facebook link'),
            textLabel: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystemMarketplaceCore.themes.details.support.contact.facebookUrl"
            }),
            children: /*#__PURE__*/_jsx(UILogo, {
              name: "facebook-messenger",
              height: SUPPORT_LINK_IMAGE_HEIGHT
            })
          }), phone && /*#__PURE__*/_jsx(SupportLinkBox, {
            href: `tel:${phoneNumber}${phoneExtension ? `p${phoneExtension}` : ''}`,
            textLabel: /*#__PURE__*/_jsx(FormattedPhoneNumber, {
              value: phoneNumber,
              extension: phoneExtension
            }),
            children: /*#__PURE__*/_jsx(UIIcon, {
              color: OZ,
              name: "calling",
              size: "lg"
            })
          })]
        })]
      })
    })]
  });
};

ThemeSupport.propTypes = {
  chatUrl: PropTypes.string,
  facebookUrl: PropTypes.string,
  getThemeEventSelectorId: PropTypes.func,
  languageTagRenderer: PropTypes.func,
  onTrackInteraction: PropTypes.func,
  phone: PropTypes.string,
  provider: PropTypes.instanceOf(ThemeProviderDetailsResponse).isRequired,
  responsive: PropTypes.bool,
  support: PropTypes.instanceOf(SupportInfo),
  supportEmail: PropTypes.string.isRequired,
  supportLanguages: PropTypes.arrayOf(PropTypes.oneOf(validSupportedLanguages)).isRequired,
  supportUrl: PropTypes.string
};
export default ThemeSupport;